import { refreshToken } from '../services/authService';
import { getStoredToken, isRefreshRejected, isTokenExpired } from './token';

/**
 * SAM-766 — the id token lives one hour and was only refreshed on page load and
 * on tab focus, so an admin working for more than an hour kept sending an
 * expired token. CRUD routes don't check it, but every /sam/* route goes
 * through MultiPoolAuthorizer, which answers 401 ("token expired") — and the
 * admin then signed the user out. These helpers refresh it before it is sent.
 */

// One refresh at a time: a page fires several requests at once and they must
// share the same /login/refresh call instead of racing it.
let inFlight: Promise<string | null> | null = null;
let lastRejected = false;

/** True when the latest refresh was turned down by the server: the session is over whatever `exp` says. */
export const refreshWasRejected = (): boolean => lastRejected;

/** Refreshes the stored tokens; resolves to the new id token, or null if refresh failed. */
export function refreshStoredToken(): Promise<string | null> {
  if (!inFlight) {
    inFlight = refreshToken()
      .then((ok) => {
        lastRejected = false;
        return ok ? getStoredToken() : null;
      })
      .catch((e) => {
        lastRejected = isRefreshRejected(e);
        return null;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

/**
 * The id token to send: the stored one, refreshed first when it is expired or
 * about to expire (60 s of skew, so it cannot lapse between here and the
 * authorizer). Falls back to the stored token when refresh fails, so the
 * response's 401 handling still decides what happens next.
 */
export async function getFreshToken(): Promise<string | null> {
  const token = getStoredToken();
  if (!token || !isTokenExpired(token, 60)) return token;
  return (await refreshStoredToken()) ?? token;
}
